// frontend/src/components/layout/UserDropdown.jsx
// User dropdown with store switcher for multi-store support

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ICON, Power, Wrench } from '../../icons';
import DisclosureCaret from '../common/DisclosureCaret';
import './UserDropdown.css';
import { storeDisplayName, storeDomainIsDistinct, userDisplayName, userEmailIsDistinct } from '../../utils/avatar';
import { useAuth } from '../../contexts/AuthContext';
import { useTabs } from '../../tabs/useTabs';
import { useStoreSwitch } from './useStoreSwitch';
import UserChip from './UserChip';
import StoreChip from './StoreChip';
import { userMenu } from './navMenus';

const UserDropdown = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { user, userContext, logout, stores, selectedStore, storesLoading } = useAuth();
  const menu = userMenu({ isStaff: userContext.user.is_staff, store: selectedStore });
  const { openTab } = useTabs();
  const { canSwitch, switchTo, switchingId, error: switchError, confirmDialog } = useStoreSwitch();
  const isSwitchingStore = switchingId !== null;

  const [storeExpanded, setStoreExpanded] = useState(false);

  // Extract user details
  const displayName = userDisplayName(user);
  const showEmail = userEmailIsDistinct(user);

  // Store details
  // Only "no store selected" earns the placeholder — a store that has just a
  // domain is named by that domain.
  const currentStoreName = selectedStore ? storeDisplayName(selectedStore) : t('userDropdown.noStore');
  const showStoreDomain = Boolean(selectedStore) && storeDomainIsDistinct(selectedStore);

  // Open via the tab system (focus-or-create), NOT raw navigate(). The
  // dropdown sits outside the tab strip; navigate() opens a tab only as a
  // side effect of a URL change, so navigating to the path you're already
  // on is a no-op and nothing opens. openTab() is idempotent.
  const handleMenuClick = (path) => {
    openTab(path);
    onClose();
  };

  const handleLogoutClick = async () => {
    onClose();
    await logout();
  };

  // The confirm dialog stays at a STABLE JSX position regardless of isOpen so
  // a click on its button doesn't trigger a remount mid-event: TopMenu's
  // outside-click listener flips isOpen during mousedown, and a dialog
  // reconciled into a different tree position would eat the click. Only the
  // dropdown body renders conditionally.
  return (
    <>
    {isOpen && (
    <div className="dropdown">
      <div className="dropdown-panel">
        {/* My Store Section */}
        {stores.length > 0 && (
          <>
            <div className={`user-dropdown-store-section ${!canSwitch ? 'single-store' : ''}`}>
              {canSwitch ? (
                <button
                  className="user-dropdown-store-header"
                  onClick={() => setStoreExpanded(!storeExpanded)}
                  disabled={isSwitchingStore || storesLoading}
                >
                  {selectedStore && (
                    <StoreChip store={selectedStore} />
                  )}
                  <div className="user-dropdown-store-details">
                    <p className="user-dropdown-store-name">{currentStoreName}</p>
                    {showStoreDomain && (
                      <p className="user-dropdown-store-domain">{selectedStore.domain}</p>
                    )}
                  </div>
                  <DisclosureCaret open={storeExpanded} className="user-dropdown-store-arrow" size={ICON.xl} />
                </button>
              ) : (
                <div className="user-dropdown-store-header">
                  {selectedStore && (
                    <StoreChip store={selectedStore} />
                  )}
                  <div className="user-dropdown-store-details">
                    <p className="user-dropdown-store-name">{currentStoreName}</p>
                    {showStoreDomain && (
                      <p className="user-dropdown-store-domain">{selectedStore.domain}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Expanded store list — inside the card. Only the stores the
                  merchant is NOT on are rows, so there is no switch-to-myself
                  case. */}
              {canSwitch && storeExpanded && (
                <div className="user-dropdown-store-list">
                  <div className="user-dropdown-store-list-divider"></div>
                  {stores
                    .filter((store) => store.id !== selectedStore?.id)
                    .map((store) => (
                      <button
                        key={store.id}
                        className="user-dropdown-store-list-item"
                        onClick={() => switchTo(store.id)}
                        disabled={isSwitchingStore}
                      >
                        <StoreChip store={store} />
                        <div className="user-dropdown-store-list-details">
                          <span className="user-dropdown-store-list-name">{storeDisplayName(store)}</span>
                          {storeDomainIsDistinct(store) && (
                            <span className="user-dropdown-store-list-domain">{store.domain}</span>
                          )}
                        </div>
                      </button>
                    ))}
                </div>
              )}
              {switchError && (
                <div className="user-dropdown-store-error" role="alert">
                  {switchError}
                </div>
              )}
            </div>

            {/* The store's own screens — outside the store card */}
            {menu.store && [menu.store.profile, ...menu.store.links].map((item) => (
              <button
                key={item.path}
                className="dropdown-button user-dropdown-store-settings-button"
                onClick={() => handleMenuClick(item.path)}
              >
                {t(item.labelKey)}
              </button>
            ))}

            <div className="dropdown-divider"></div>
          </>
        )}

        {/* User Info Section */}
        <div className="user-dropdown-info">
          <UserChip user={user} />
          <div className="user-dropdown-details">
            <p className="user-dropdown-name">{displayName}</p>
            {showEmail && (
              <p className="user-dropdown-email">{user.email}</p>
            )}
          </div>
        </div>

        {/* Menu Items */}
        <div className="dropdown-menu">
          <button className="dropdown-button" onClick={() => handleMenuClick(menu.userProfile.path)}>
            {t(menu.userProfile.labelKey)}
          </button>

          <div className="dropdown-divider"></div>

          {menu.tools.map((item) => (
            <button
              key={item.path}
              className={`dropdown-button${item.staffOnly ? ' dropdown-button--staff' : ''}`}
              onClick={() => handleMenuClick(item.path)}
            >
              {t(item.labelKey)}
              {item.staffOnly && <Wrench size={ICON.sm} className="dropdown-staff-icon" />}
            </button>
          ))}

          <div className="dropdown-divider"></div>

          <button
            className="dropdown-button user-dropdown-logout"
            onClick={handleLogoutClick}
          >
            <Power size={ICON.xl} className="user-dropdown-logout-icon" />
            {t('userDropdown.logout')}
          </button>
        </div>
      </div>

    </div>
    )}
    {confirmDialog}
    </>
  );
};

export default UserDropdown;
